import {
  VisitorSessionListItem,
  VisitorSessionSourceSummary,
} from '../models/visitor-session';

export function shortId(value?: string | null, length = 8): string {
  if (!value) {
    return '-';
  }
  if (value.length <= length) {
    return value;
  }
  return `${value.slice(0, length)}…`;
}

export function formatIdentity(
  identified?: boolean | null
): 'Identified' | 'Anonymous' {
  return identified ? 'Identified' : 'Anonymous';
}

export function safeDash(value?: string | number | null): string {
  if (value === null || value === undefined || value === '') {
    return '-';
  }
  return String(value);
}

function sourceToText(
  source?: VisitorSessionSourceSummary | string | null
): string {
  if (!source) {
    return '';
  }
  if (typeof source === 'string') {
    return source.trim();
  }

  const parts: string[] = [];
  const fields: Array<keyof VisitorSessionSourceSummary> = [
    'utmSource',
    'utmMedium',
    'utmCampaign',
    'utmTerm',
    'utmContent',
    'referrer',
  ];

  for (const field of fields) {
    const value = source[field];
    if (typeof value === 'string' && value.trim()) {
      parts.push(value.trim());
    }
  }

  return parts.join(' / ');
}

export function formatLanding(session?: VisitorSessionListItem | null): string {
  if (!session) {
    return '-';
  }
  const path = session.landingPath?.trim();
  const key = session.landingKey?.trim();
  if (path && key) {
    return `${path} (${key})`;
  }
  return safeDash(path || key);
}

export function formatSourceSummary(
  session?: VisitorSessionListItem | null
): string {
  if (!session) {
    return '-';
  }
  const summary =
    sourceToText(session.sourceSummary) ||
    sourceToText(session.firstTouchSource);
  return safeDash(summary);
}

export function formatEventTimestamp(event: {
  occurredAt?: string | null;
  timestamp?: string | null;
}): string | null {
  return event.occurredAt || event.timestamp || null;
}

export function formatEventMetadata(metadata: unknown): string {
  if (metadata === null || metadata === undefined || metadata === '') {
    return '-';
  }
  if (typeof metadata === 'string') {
    return metadata;
  }
  try {
    return JSON.stringify(metadata);
  } catch {
    return String(metadata);
  }
}

function eventActionMap(event: {
  metadata?: unknown;
  payload?: unknown;
  actionDetails?: unknown;
  action?: unknown;
}): Record<string, unknown> {
  const raw = event.metadata ?? event.payload ?? event.actionDetails ?? event.action;
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    return raw as Record<string, unknown>;
  }
  return {};
}

export function formatEventSummary(event: {
  eventType?: string | null;
  type?: string | null;
  landingKey?: string | null;
  landingPath?: string | null;
  metadata?: unknown;
  payload?: unknown;
  actionDetails?: unknown;
  action?: unknown;
}): string {
  const type = String(event.eventType || event.type || '');
  const action = eventActionMap(event);
  const field = (key: string): string =>
    action[key] == null || action[key] === '' ? '' : String(action[key]);

  switch (type) {
    case 'LANDING_VIEWED':
      return `Landing ${field('landingKey') || event.landingKey || event.landingPath || ''}`.trim();
    case 'SECTION_VIEWED':
      return field('sectionId').startsWith('scroll-')
        ? `Scroll ${field('depthPercent') || field('sectionId').replace('scroll-', '')}%`
        : `Section ${field('sectionId')}`;
    case 'PLAN_SELECTION_VIEWED':
      return `Plans ${field('source') || field('route') || ''}`.trim();
    case 'PLAN_SELECTED':
      return `Plan selected ${field('planKind') || field('planId') || field('planType')}`.trim();
    case 'PAGE_VIEWED':
      return `Page ${field('routeKind') || ''} ${field('route') || event.landingPath || ''}`.trim();
    case 'CTA_CLICKED':
      return `Click ${field('ctaId')}`;
    case 'VIDEO_PLAYED':
      return `Video play ${field('videoLabel') || field('videoId')}`;
    case 'VIDEO_PROGRESS':
      return `Video ${field('percent')}% ${field('videoLabel') || field('videoId')}`.trim();
    case 'AUTH_STEP_VIEWED':
      return field('authMethod')
        ? `Auth ${field('authStep')} / ${field('authMethod')}`
        : `Auth ${field('authStep')}`;
    case 'SESSION_ENDED':
      return `Session ended (${field('reason') || 'close'})`;
    default:
      return type || '-';
  }
}

export function summarizeLandingJourney(
  events: Array<{
    eventType?: string | null;
    type?: string | null;
    landingKey?: string | null;
    landingPath?: string | null;
    metadata?: unknown;
    payload?: unknown;
    actionDetails?: unknown;
    action?: unknown;
  }>
): string[] {
  const landings: string[] = [];
  const pages: string[] = [];
  const sections: string[] = [];
  let maxScroll = 0;
  let maxVideo = 0;
  let lastAuth = '';
  let lastPlan = '';
  const hubSteps: string[] = [];
  let ended = '';

  for (const event of events) {
    const type = String(event.eventType || event.type || '');
    const action = eventActionMap(event);
    const field = (key: string): string =>
      action[key] == null ? '' : String(action[key]);

    if (type === 'LANDING_VIEWED') {
      const key = field('landingKey') || event.landingKey || event.landingPath || '';
      if (key && !landings.includes(key)) {
        landings.push(key);
      }
    }
    if (type === 'PAGE_VIEWED' || type === 'PLAN_SELECTION_VIEWED') {
      const route = field('routeKind') || field('route') || event.landingPath || type;
      if (route && !pages.includes(route)) {
        pages.push(route);
      }
    }
    if (type === 'SECTION_VIEWED') {
      const sectionId = field('sectionId');
      if (sectionId.startsWith('scroll-')) {
        const depth = Number(field('depthPercent') || sectionId.replace('scroll-', ''));
        if (depth > maxScroll) {
          maxScroll = depth;
        }
      } else if (sectionId && !sections.includes(sectionId)) {
        sections.push(sectionId);
      }
    }
    if (type === 'VIDEO_PROGRESS') {
      const percent = Number(field('percent') || 0);
      if (percent > maxVideo) {
        maxVideo = percent;
      }
    }
    if (type === 'VIDEO_PLAYED' && maxVideo < 1) {
      maxVideo = 1;
    }
    if (type === 'PLAN_SELECTED') {
      lastPlan = field('planKind') || field('planId') || lastPlan;
    }
    if (type === 'SECTION_VIEWED' && field('sectionId').startsWith('hub-setup:')) {
      const step = field('sectionId').replace('hub-setup:', '');
      if (step && !hubSteps.includes(step)) {
        hubSteps.push(step);
      }
    }
    if (type === 'AUTH_STEP_VIEWED') {
      lastAuth = field('authMethod') || field('authStep') || lastAuth;
    }
    if (type === 'CTA_CLICKED' && field('ctaId') === 'login') {
      lastAuth = lastAuth || 'login_click';
    }
    if (type === 'SESSION_ENDED') {
      ended = field('reason') || 'pagehide';
    }
  }

  const lines: string[] = [];
  if (landings.length) {
    lines.push(`Landings: ${landings.join(' → ')}`);
  }
  if (pages.length) {
    lines.push(`Pages: ${pages.join(' → ')}`);
  }
  if (sections.length) {
    lines.push(`Sections viewed: ${sections.length}`);
  }
  if (maxScroll) {
    lines.push(`Max scroll: ${maxScroll}%`);
  }
  if (maxVideo === 1) {
    lines.push('Video: played');
  } else if (maxVideo > 1) {
    lines.push(`Video: ${maxVideo}%`);
  }
  if (lastAuth) {
    lines.push(`Auth farthest: ${lastAuth}`);
  }
  if (lastPlan) {
    lines.push(`Plan selected: ${lastPlan}`);
  }
  if (hubSteps.length) {
    lines.push(`Hub setup: ${hubSteps.join(' → ')}`);
  }
  if (ended) {
    lines.push(`Ended: ${ended}`);
  }
  if (!lines.length) {
    lines.push('No session engagement events recorded.');
  }
  return lines;
}
